import {
  BadgeSecondarySubtle,
  ButtonSmallSecondary,
} from "@studio3t/generic-components";
import { useState } from "react";
import { OverlayScroll } from "@/components/OverlayScroll";
import { RawConfigModal } from "@/components/RawConfigModal";
import {
  ConfigActions,
  ConfigBadges,
  ConfigBody,
  ConfigItem,
  ConfigKey,
  ConfigVal,
  SectionCard,
} from "@/components/SectionCard";
import type { Connector, JobMetadata, Workspace } from "@/types/api";

interface ConfigurationSectionProps {
  job: JobMetadata;
  pipeline: Connector | null | undefined;
  /** The workspace the job was deployed from, when one records it: its
   *  created/modified stamps are the pipeline's own lifecycle (the
   *  workspace detail's Info card, folded in here). */
  workspace?: Workspace | null;
}

function formatDateTime(isoString: string | null | undefined): string {
  if (!isoString) return "—";
  try {
    return new Date(isoString).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return isoString;
  }
}

function normalizeState(state: string): string {
  return state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
}

// source encoding → the distinct sink encodings, bson when unset.
function EncodingItem({ pipeline }: { pipeline: Connector }) {
  const sinks = [
    ...new Set(pipeline.sinks.map((s) => s.output_encoding || "bson")),
  ];
  return (
    <ConfigItem>
      <ConfigKey>Encoding</ConfigKey>
      <ConfigVal>
        {pipeline.source.output_encoding || "bson"} → {sinks.join(", ")}
      </ConfigVal>
    </ConfigItem>
  );
}

function LinkedServicesItem({ services }: { services: string[] }) {
  return (
    <ConfigItem>
      <ConfigKey>Linked services</ConfigKey>
      {services.length > 0 ? (
        <ConfigBadges>
          {services.map((svc) => (
            <BadgeSecondarySubtle key={svc}>{svc}</BadgeSecondarySubtle>
          ))}
        </ConfigBadges>
      ) : (
        <ConfigVal>—</ConfigVal>
      )}
    </ConfigItem>
  );
}

// The "Raw configuration" button and the modal it opens.
function RawConfiguration({ pipeline }: { pipeline: Connector }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ConfigActions>
        <ButtonSmallSecondary
          text="Raw configuration"
          active={open}
          onClick={() => setOpen(true)}
        />
      </ConfigActions>
      {open && (
        <RawConfigModal
          isOpen
          value={JSON.stringify(pipeline, null, 2)}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function LifecycleItems({ workspace }: { workspace: Workspace }) {
  return (
    <>
      <ConfigItem>
        <ConfigKey>Created on</ConfigKey>
        <ConfigVal>{formatDateTime(workspace.created_at)}</ConfigVal>
      </ConfigItem>
      <ConfigItem>
        <ConfigKey>Modified on</ConfigKey>
        <ConfigVal>{formatDateTime(workspace.updated_at)}</ConfigVal>
      </ConfigItem>
    </>
  );
}

export function ConfigurationSection({
  job,
  pipeline,
  workspace,
}: ConfigurationSectionProps) {
  return (
    <SectionCard title="Configuration">
      {/* Custom scrollbars when a long value (datetime, encoding, service name)
          is wider than the card on a narrow screen. ConfigBody keeps its flex
          column layout inside; OverlayScrollbars manages the overflow. */}
      <OverlayScroll>
        <ConfigBody>
          {workspace && <LifecycleItems workspace={workspace} />}

          <ConfigItem>
            <ConfigKey>Started at</ConfigKey>
            <ConfigVal>{formatDateTime(job.started_at)}</ConfigVal>
          </ConfigItem>

          {job.stopped_at && (
            <ConfigItem>
              <ConfigKey>Stopped at</ConfigKey>
              <ConfigVal>{formatDateTime(job.stopped_at)}</ConfigVal>
            </ConfigItem>
          )}

          <ConfigItem>
            <ConfigKey>Desired state</ConfigKey>
            <ConfigVal>{normalizeState(job.desired_state)}</ConfigVal>
          </ConfigItem>

          {pipeline && <EncodingItem pipeline={pipeline} />}

          <ConfigItem>
            <ConfigKey>Checkpoint</ConfigKey>
            <ConfigVal>
              {pipeline?.checkpoint?.enabled ? "Enabled" : "Disabled"}
            </ConfigVal>
          </ConfigItem>

          <LinkedServicesItem services={job.linked_services} />

          {pipeline && <RawConfiguration pipeline={pipeline} />}
        </ConfigBody>
      </OverlayScroll>
    </SectionCard>
  );
}
